import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, FileText, User, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import PatientManagement from '@/components/PatientManagement';
import FormulierungshilfenSearch from '@/components/FormulierungshilfenSearch';
import PflegeplanEditor from '@/components/PflegeplanEditor';
interface Patient {
  id?: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  geschlecht: string;
  zimmernummer: string;
  aufnahmedatum: string;
  diagnose: string;
  besonderheiten: string;
}
interface Formulierungshilfe {
  id: string;
  kategorie: string;
  lebensaktivitaet: string;
  text: string;
  stichwort: string;
}
interface Pflegeplan {
  id?: string;
  patient_id: string;
  titel: string;
  lebensaktivitaet: string;
  ressourcen: string[];
  probleme: string[];
  ziele: string[];
  massnahmen: string[];
}
const Index = () => {
  const [activeTab, setActiveTab] = useState('patients');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [formulierungshilfen, setFormulierungshilfen] = useState<Formulierungshilfe[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('alle');
  const [selectedActivity, setSelectedActivity] = useState('alle');
  const [pflegeplan, setPflegeplan] = useState<Pflegeplan>({
    patient_id: '',
    titel: '',
    lebensaktivitaet: '',
    ressourcen: [],
    probleme: [],
    ziele: [],
    massnahmen: []
  });
  const {
    toast
  } = useToast();
  const lebensaktivitaeten = ['Vitale Funktionen aufrechterhalten können', 'Sich bewegen können', 'Sich sauber halten und kleiden können', 'Essen und trinken können', 'Ausscheiden können', 'Kommunizieren können', 'Ruhen und schlafen können', 'Sich beschäftigen können', 'Für Sicherheit sorgen können', 'Soziale Bereiche des Lebens sichern können'];

  // Patientendaten laden
  const loadPatients = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('patienten_2025_12_03_21_33').select('*').order('nachname');
      if (error) throw error;
      setPatients(data || []);
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Patienten konnten nicht geladen werden.',
        variant: 'destructive'
      });
    }
  };

  // Formulierungshilfen laden
  const loadFormulierungshilfen = async () => {
    try {
      let query = supabase.from('formulierungshilfen_2025_12_03_21_33').select('*');
      if (selectedCategory !== 'alle') {
        query = query.eq('kategorie', selectedCategory);
      }
      if (selectedActivity !== 'alle') {
        query = query.eq('lebensaktivitaet', selectedActivity);
      }
      if (searchTerm) {
        query = query.or(`text.ilike.%${searchTerm}%,stichwort.ilike.%${searchTerm}%`);
      }
      const {
        data,
        error
      } = await query.order('lebensaktivitaet').order('kategorie');
      if (error) throw error;
      setFormulierungshilfen(data || []);
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Formulierungshilfen konnten nicht geladen werden.',
        variant: 'destructive'
      });
    }
  };

  // Patient speichern
  const savePatient = async (patient: Patient) => {
    try {
      const {
        data,
        error
      } = await supabase.from('patienten_2025_12_03_21_33').insert([patient]).select().single();
      if (error) throw error;
      toast({
        title: 'Erfolg',
        description: 'Patient wurde erfolgreich gespeichert.'
      });
      loadPatients();
      return data;
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Patient konnte nicht gespeichert werden.',
        variant: 'destructive'
      });
    }
  };

  // Pflegeplan speichern
  const savePflegeplan = async () => {
    if (!selectedPatient || !pflegeplan.titel || !pflegeplan.lebensaktivitaet) {
      toast({
        title: 'Fehler',
        description: 'Bitte füllen Sie alle Pflichtfelder aus.',
        variant: 'destructive'
      });
      return;
    }
    try {
      const {
        error
      } = await supabase.from('pflegeplaene_2025_12_03_21_33').insert([{
        ...pflegeplan,
        patient_id: selectedPatient.id!
      }]);
      if (error) throw error;
      toast({
        title: 'Erfolg',
        description: 'Pflegeplan wurde erfolgreich gespeichert.'
      });

      // Pflegeplan zurücksetzen
      setPflegeplan({
        patient_id: '',
        titel: '',
        lebensaktivitaet: '',
        ressourcen: [],
        probleme: [],
        ziele: [],
        massnahmen: []
      });
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Pflegeplan konnte nicht gespeichert werden.',
        variant: 'destructive'
      });
    }
  };

  // Formulierung zu Pflegeplan hinzufügen
  const addToPflegeplan = (formulierung: Formulierungshilfe) => {
    const category = formulierung.kategorie;
    const text = formulierung.text;
    setPflegeplan(prev => {
      const updated = {
        ...prev
      };
      switch (category) {
        case 'ressource':
          if (!updated.ressourcen.includes(text)) {
            updated.ressourcen = [...updated.ressourcen, text];
          }
          break;
        case 'problem':
          if (!updated.probleme.includes(text)) {
            updated.probleme = [...updated.probleme, text];
          }
          break;
        case 'ziel':
          if (!updated.ziele.includes(text)) {
            updated.ziele = [...updated.ziele, text];
          }
          break;
        case 'massnahme':
          if (!updated.massnahmen.includes(text)) {
            updated.massnahmen = [...updated.massnahmen, text];
          }
          break;
      }
      return updated;
    });
    toast({
      title: 'Hinzugefügt',
      description: `Formulierung wurde zum Pflegeplan hinzugefügt.`
    });
  };

  // PDF Export (vereinfacht)
  const exportToPDF = () => {
    if (!selectedPatient || !pflegeplan.titel) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie einen Patienten und erstellen Sie einen Pflegeplan.',
        variant: 'destructive'
      });
      return;
    }

    // Vereinfachter PDF-Export als Text-Download
    const content = `
PFLEGEPLANUNG

Patient: ${selectedPatient.vorname} ${selectedPatient.nachname}
Geburtsdatum: ${selectedPatient.geburtsdatum}
Zimmer: ${selectedPatient.zimmernummer}
Diagnose: ${selectedPatient.diagnose}

Pflegeplan: ${pflegeplan.titel}
Lebensaktivität: ${pflegeplan.lebensaktivitaet}

RESSOURCEN:
${pflegeplan.ressourcen.map(r => `• ${r}`).join('\n')}

PROBLEME:
${pflegeplan.probleme.map(p => `• ${p}`).join('\n')}

ZIELE:
${pflegeplan.ziele.map(z => `• ${z}`).join('\n')}

MASSNAHMEN:
${pflegeplan.massnahmen.map(m => `• ${m}`).join('\n')}

Erstellt am: ${new Date().toLocaleDateString('de-DE')}
    `;
    const blob = new Blob([content], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Pflegeplan_${selectedPatient.nachname}_${selectedPatient.vorname}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: 'Export erfolgreich',
      description: 'Pflegeplan wurde als Textdatei heruntergeladen.'
    });
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Pflegeplanungs-Tool</h1>
          <p className="text-lg text-gray-600">Formulierungshilfen für die Pflegeplanung. Erstellt von Krankenbruder.ch</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="patients" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Patienten
            </TabsTrigger>
            <TabsTrigger value="formulierungen" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Formulierungshilfen
            </TabsTrigger>
            <TabsTrigger value="pflegeplan" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Pflegeplan
            </TabsTrigger>
          </TabsList>

          <TabsContent value="patients" className="space-y-6">
            <PatientManagement patients={patients} onLoadPatients={loadPatients} onSavePatient={savePatient} onSelectPatient={setSelectedPatient} selectedPatient={selectedPatient} />
          </TabsContent>

          <TabsContent value="formulierungen" className="space-y-6">
            <FormulierungshilfenSearch formulierungshilfen={formulierungshilfen} searchTerm={searchTerm} setSearchTerm={setSearchTerm} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} selectedActivity={selectedActivity} setSelectedActivity={setSelectedActivity} lebensaktivitaeten={lebensaktivitaeten} onLoadFormulierungshilfen={loadFormulierungshilfen} onAddToPflegeplan={addToPflegeplan} />
          </TabsContent>

          <TabsContent value="pflegeplan" className="space-y-6">
            <PflegeplanEditor selectedPatient={selectedPatient} pflegeplan={pflegeplan} setPflegeplan={setPflegeplan} lebensaktivitaeten={lebensaktivitaeten} onSavePflegeplan={savePflegeplan} onExportToPDF={exportToPDF} />
          </TabsContent>
        </Tabs>
      </div>
    </div>;
};
export default Index;